<template>
  <div ref="contentTile" class="ProductCard ProductCard--Custom" data-ht-component="collection-content-tile">
    <div class="ProductCard__Media ProductCard__Media--Custom" v-if="image || video" :data-colspan="customColspan" :data-content-tile="title">
      <component
        :is="url ? 'a' : 'div'"
        :href="url"
        :data-ht-element="url.includes('stl-product') ? 'shop-the-look-content-tile' : 'content-tile-cta'"
        :ref="
          (media) => {
            initHotspots(media);
          }
        "
      >
        <video v-if="video" loop autoplay :controls="showVideoNativeControls ? true : false" playsinline muted preload="metadata" :poster="image">
          <source :src="videoSrc" type="video/mp4" />
          <img v-if="image" :src="image" :alt="title" loading="lazy" />
        </video>
        <picture v-else>
          <source :srcset="image" />
          <img :src="image" :alt="title" loading="lazy" />
        </picture>
        <div
          :class="[
            {
              'MediaContent--Button': isNotEmpty(cta),
              'MediaContent--Title': isNotEmpty(title),
            },
            positionClass(position),
            'ProductCard__MediaContent',
          ]"
        >
          <p class="h4 MediaContent--Copy" v-if="isNotEmpty(title)" :data-color="title.color" v-html="title.value"></p>
          <div class="button button--rounded button--tertiary-b" v-if="isNotEmpty(cta)" v-html="cta.value"></div>
        </div>
      </component>
      <div v-if="video && showVideoControls" :class="[{ 'video-controls--paused': videoPaused }, 'js--video-controls']" @click="toggleVideo">
        <Icon icon="video-play" />
        <Icon icon="video-pause" />
      </div>
    </div>
    <template v-if="isNotEmpty(caption)">
      <p class="p4" data-color="night-sky-dark" v-html="caption.value"></p>
    </template>
    <template v-else>
      <h4 class="h4 ProductCard__Custom-Title" :class="{ padded: isNotEmpty(customDesc) }" v-if="customTitle" v-html="customTitle"></h4>
      <p class="p5 ProductCard__Custom-Desc" data-color="night-sky-dark" v-if="customDesc" v-html="customDesc"></p>
    </template>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { getCurrentView, onViewChange } from '@utils/breakpoints';
import Icon from './Icon.vue';
import Hotspots from '@modules/Hotspots';

const props = defineProps({
  content: Object,
});

const video = ref(false);
const videoSrc = ref(false);
const image = ref(false);
const title = ref(false);
const url = ref(false);
const cta = ref(false);
const caption = ref(false);
const position = ref(false);
const customTitle = ref(false);
const customDesc = ref(false);
const customColspan = ref(false);
const showVideoControls = ref(false);
const showVideoNativeControls = ref(false);
const videoPaused = ref(false);
const contentTile = ref(false);

const hasHotspots = () => {
  return props?.content?.hotspots ? 'stl-hotspots-media' : false;
};
const positionClass = (position) => (isNotEmpty(title.value) ? `MediaContent--${position}` : '');
const cleanUrl = (url) => (url.includes('brooklinen.com') ? url.split('.com').at(-1) : url);

const toggleVideo = (event) => {
  videoPaused.value = !videoPaused.value;
  const video = event.target.closest('.ProductCard').querySelector('video');
  videoPaused.value ? video.pause() : video.play();
};

const determineMediaSrc = () => {
  let src;
  if (getCurrentView() == 'sml' || getCurrentView() == 'med') {
    src = props.content?.video ? props?.content?.video_mobile : props?.content?.image_mobile;
  } else {
    src = props.content?.video ? props?.content?.video : props?.content?.image;
  }
  props.content?.video ? (videoSrc.value = src) : (image.value = src);
};

onViewChange(() => {
  determineMediaSrc();
  setTimeout(() => {
    const videoEl = document.querySelector(`source[src="${videoSrc.value}"]`);
    if (videoEl) videoEl.parentElement.load();
  }, 0);
}, 'lrg');

onMounted(() => {
  if (isNotEmpty(props.content?.image)) {
    image.value = props.content?.image;
  }
  if (isNotEmpty(props.content?.video)) {
    video.value = props.content?.video;
    showVideoControls.value = props.content?.videoControlsEnabled == 'true' ? true : false;

    if (props.content?.videoControlsNativeEnabled == 'true') {
      showVideoControls.value = false;
      showVideoNativeControls.value = true;
    } else {
      showVideoNativeControls.value = false;
    }
  }

  determineMediaSrc();
  url.value = cleanUrl(props.content?.url) || '';
  title.value = props.content?.title?.value || props.content?.title?.color || '';
  cta.value = props.content?.cta || '';
  caption.value = props.content?.caption || '';
  position.value = props.content?.position || '';
  customTitle.value = props.content?.custom?.title || '';
  customDesc.value = props.content?.custom?.description || '';
  customColspan.value = props.content?.colspan || 1;
});

const initHotspots = (media) => {
  if (hasHotspots()) {
    new Hotspots(media, props?.content?.hotspots);
  }
};

const isNotEmpty = (string) => string.value !== '';
</script>
